import React, { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { TooltipProps } from "recharts";

import { PROJECTED_OPACITY, SERIES_COLORS } from "../../constants/chartTheme";
import { formatINR } from "../../utils/format";
import "./ProjectedActualDonut.css";

type Props = {
  projectedRevenue: number;
  actualRevenue: number;
  projectedProfit: number;
  actualProfit: number;
};

type RingDatum = {
  name: string;
  value: number;
  tone: "actual" | "remaining";
};

/**
 * Builds one ring: the actual arc plus the gap left to the projection.
 *
 * Guards the two cases that would otherwise render nonsense — a zero
 * projection (no denominator, so nothing to be a proportion of) and a
 * negative profit (an arc cannot be negative, so it is clamped to empty and
 * the real figure is carried in the label instead).
 */
function buildRing(actual: number, projected: number, label: string): RingDatum[] {
  const safeProjected = Math.max(projected, 0);
  const safeActual = Math.max(actual, 0);

  if (safeProjected <= 0) {
    // Nothing projected: show an empty track rather than dividing by zero.
    return [{ name: `${label} (no projection)`, value: 1, tone: "remaining" }];
  }

  const achieved = Math.min(safeActual, safeProjected);
  const remaining = Math.max(safeProjected - achieved, 0);
  return [
    { name: `Actual ${label.toLowerCase()}`, value: achieved, tone: "actual" },
    { name: `Remaining to projection`, value: remaining, tone: "remaining" },
  ];
}

function pct(actual: number, projected: number): string {
  if (projected <= 0) return "—";
  return `${Math.round((actual / projected) * 100)}%`;
}

function DonutTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className="projected-actual-donut__tooltip">
      <div className="projected-actual-donut__tooltip-name">{item.name}</div>
      <div className="projected-actual-donut__tooltip-value">{formatINR(Number(item.value) || 0)}</div>
    </div>
  );
}

/**
 * Outer ring = revenue, inner ring = profit. Solid arc is actual, faint arc
 * is the remainder of the projection (the wireframe's light vs shaded area).
 */
export const ProjectedActualDonut: React.FC<Props> = ({
  projectedRevenue,
  actualRevenue,
  projectedProfit,
  actualProfit,
}) => {
  const revenueRing = useMemo(
    () => buildRing(actualRevenue, projectedRevenue, "Revenue"),
    [actualRevenue, projectedRevenue]
  );
  const profitRing = useMemo(
    () => buildRing(actualProfit, projectedProfit, "Profit"),
    [actualProfit, projectedProfit]
  );

  const profitIsLoss = actualProfit < 0;

  return (
    <div className="projected-actual-donut">
      <div className="projected-actual-donut__chart">
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={revenueRing}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={78}
              outerRadius={100}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              {revenueRing.map((entry, index) => (
                <Cell
                  key={`revenue-${index}`}
                  fill={SERIES_COLORS.revenue}
                  fillOpacity={entry.tone === "actual" ? 1 : PROJECTED_OPACITY}
                />
              ))}
            </Pie>
            <Pie
              data={profitRing}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={48}
              outerRadius={70}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              {profitRing.map((entry, index) => (
                <Cell
                  key={`profit-${index}`}
                  fill={profitIsLoss ? SERIES_COLORS.expense : SERIES_COLORS.profit}
                  fillOpacity={entry.tone === "actual" ? 1 : PROJECTED_OPACITY}
                />
              ))}
            </Pie>
            <Tooltip
              content={<DonutTooltip />}
              wrapperStyle={{ outline: "none", zIndex: 20 }}
              allowEscapeViewBox={{ x: true, y: true }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <dl className="projected-actual-donut__legend">
        <div className="projected-actual-donut__legend-row">
          <span
            className="projected-actual-donut__swatch"
            style={{ background: SERIES_COLORS.revenue }}
            aria-hidden
          />
          <dt>Revenue</dt>
          <dd>
            {formatINR(actualRevenue)} <span className="eventos-subtle">of {formatINR(projectedRevenue)}</span>{" "}
            <strong>{pct(actualRevenue, projectedRevenue)}</strong>
          </dd>
        </div>
        <div className="projected-actual-donut__legend-row">
          <span
            className="projected-actual-donut__swatch"
            style={{ background: profitIsLoss ? SERIES_COLORS.expense : SERIES_COLORS.profit }}
            aria-hidden
          />
          <dt>Profit</dt>
          <dd>
            {formatINR(actualProfit)} <span className="eventos-subtle">of {formatINR(projectedProfit)}</span>{" "}
            <strong>{profitIsLoss ? "Loss" : pct(actualProfit, projectedProfit)}</strong>
          </dd>
        </div>
      </dl>
      <p className="eventos-subtle projected-actual-donut__note">
        Outer ring revenue · inner ring profit. Solid is actual, faint is the rest of the projection.
      </p>
    </div>
  );
};

export default ProjectedActualDonut;
